/** Global app state. Zustand keeps this small enough to read in one sitting. */

import { create } from 'zustand'
import {
  ApiError,
  api,
  getToken,
  setToken,
  type CategoryDef,
  type Household,
  type Reminder,
  type User,
} from './api'
import { getLocationProvider, haversine, type Fix } from './location'
import { hasOnboarded, markOnboarded } from './onboarding'
import { showNudge } from './notify'

export type Tab = 'list' | 'map' | 'household' | 'profile'

interface State {
  booted: boolean
  user: User | null
  reminders: Reminder[]
  categories: CategoryDef[]
  households: Household[]
  fix: Fix | null
  nearbyIds: Set<string>
  tab: Tab
  watching: boolean
  error: string | null
  locationError: string | null
  tierLimitHit: boolean
  /** Has *this browser* taken this account through the permission steps?
   * Reactive mirror of the localStorage flag in `onboarding.ts` — App gates the
   * whole shell on it, so it cannot be read straight from storage. */
  onboarded: boolean

  boot: () => Promise<void>
  signIn: (email: string, password: string) => Promise<void>
  signUp: (email: string, password: string, name: string) => Promise<void>
  signOut: () => void
  refresh: () => Promise<void>
  /** Re-read the signed-in user. Cheap, and the only way `email_verified`
   * changes without a reload once a confirmation link is clicked. */
  refreshUser: () => Promise<void>
  refreshHouseholds: () => Promise<void>
  refreshCategories: () => Promise<void>
  createCategory: (body: { label: string; color: string; icon: string }) => Promise<CategoryDef>
  updateCategory: (
    id: string,
    patch: Partial<Pick<CategoryDef, 'label' | 'color' | 'icon' | 'sort_order'>>,
  ) => Promise<void>
  deleteCategory: (id: string) => Promise<void>
  setTab: (tab: Tab) => void
  startWatching: () => void
  stopWatching: () => void
  pushFix: (fix: Fix, force?: boolean) => Promise<void>
  setError: (msg: string | null) => void
  clearTierLimit: () => void
  /** Finish onboarding for the signed-in user and remember it on this device. */
  completeOnboarding: () => void
}

let stopWatch: (() => void) | null = null
// Reporting every GPS tick would hammer the API for no benefit; the geofence
// only needs to know when the user has meaningfully moved.
let lastReportAt = 0
const MIN_REPORT_INTERVAL_MS = 15_000

// `watchPosition` re-fires every second or two even when the phone is sitting
// still, and each callback builds a fresh Fix object. Publishing every one of
// those to the store gave `fix` a new identity on every tick, which remounted
// the Leaflet map and re-ran every effect that depends on location — the map
// reset its zoom mid-pinch and the store list refetched itself into a flicker.
// Only publish a fix the user would actually notice.
const FIX_MOVE_THRESHOLD_M = 25
const FIX_MAX_AGE_MS = 60_000

/**
 * Should this fix replace the one the UI is currently rendering?
 *
 * True on the first fix, once the user has actually moved, when the reading
 * gets meaningfully more precise, or when the held fix is simply stale.
 * Deliberately generous — a false negative costs a slightly out-of-date dot,
 * a false positive costs the entire map.
 */
function isMeaningfulMove(prev: Fix | null, next: Fix): boolean {
  if (!prev) return true
  if (haversine(prev.lat, prev.lon, next.lat, next.lon) >= FIX_MOVE_THRESHOLD_M) return true
  if (next.at - prev.at >= FIX_MAX_AGE_MS) return true
  // A much tighter accuracy radius is worth redrawing for; a looser one is not.
  const before = prev.accuracyM
  const after = next.accuracyM
  if (before != null && after != null && after < before / 2) return true
  return false
}

export const useStore = create<State>((set, get) => ({
  booted: false,
  user: null,
  onboarded: false,
  reminders: [],
  categories: [],
  households: [],
  fix: null,
  nearbyIds: new Set(),
  tab: 'list',
  watching: false,
  error: null,
  locationError: null,
  tierLimitHit: false,

  async boot() {
    if (!getToken()) {
      set({ booted: true })
      return
    }
    try {
      const user = await api.me()
      set({ user, onboarded: hasOnboarded(user.id) })
      // Categories first: every reminder card resolves its colour and glyph
      // through them, so loading them after would flash an Uncategorized state.
      await get().refreshCategories()
      await get().refresh()
      await get().refreshHouseholds()
    } catch (err) {
      // An expired token shouldn't look like a crash — just sign out quietly.
      if (!(err instanceof ApiError && err.status === 401)) {
        set({ error: err instanceof Error ? err.message : String(err) })
      }
      setToken(null)
      set({ user: null })
    } finally {
      set({ booted: true })
    }
  },

  async refreshUser() {
    if (!getToken()) return
    set({ user: await api.me() })
  },

  async signIn(email, password) {
    const { access_token } = await api.login(email, password)
    setToken(access_token)
    const user = await api.me()
    // A returning user on a browser that has already granted permissions skips
    // straight back in; a new browser goes through setup again, because that is
    // where the permission grant actually lives.
    set({ user, error: null, onboarded: hasOnboarded(user.id) })
    await get().refreshCategories()
    await get().refresh()
    await get().refreshHouseholds()
  },

  async signUp(email, password, name) {
    const { access_token } = await api.register(email, password, name)
    setToken(access_token)
    const user = await api.me()
    // Never `onboarded` on a fresh registration — the permission steps are the
    // rest of this flow, and skipping them is the whole of APP-12.
    set({ user, error: null, onboarded: false })
  },

  signOut() {
    get().stopWatching()
    setToken(null)
    set({
      user: null,
      onboarded: false,
      reminders: [],
      categories: [],
      households: [],
      nearbyIds: new Set(),
      tab: 'list',
    })
  },

  async refresh() {
    const { fix } = get()
    try {
      const reminders = await api.listReminders(fix?.lat, fix?.lon)
      set({ reminders, error: null })
    } catch (err) {
      set({ error: err instanceof Error ? err.message : String(err) })
    }
  },

  async refreshHouseholds() {
    try {
      set({ households: await api.households() })
    } catch (err) {
      set({ error: err instanceof Error ? err.message : String(err) })
    }
  },

  async refreshCategories() {
    try {
      set({ categories: await api.categories() })
    } catch (err) {
      set({ error: err instanceof Error ? err.message : String(err) })
    }
  },

  // The three mutators re-throw: a category write is always driven by a form that
  // needs to show its own failure (a duplicate label, say) rather than the global
  // toast. They refresh in place so callers never have to.
  async createCategory(body) {
    const created = await api.createCategory(body)
    await get().refreshCategories()
    return created
  },

  async updateCategory(id, patch) {
    await api.updateCategory(id, patch)
    await get().refreshCategories()
    // Reminders carry an embedded category_detail; re-pull so a rename or
    // recolour also lands on anything rendered from someone else's copy.
    await get().refresh()
  },

  async deleteCategory(id) {
    await api.deleteCategory(id)
    await get().refreshCategories()
    // Without this the deleted category's embedded copy would keep rendering
    // instead of falling back to Uncategorized.
    await get().refresh()
  },

  setTab(tab) {
    set({ tab })
  },

  startWatching() {
    if (get().watching) return
    const provider = getLocationProvider()
    stopWatch = provider.watch(
      (fix) => {
        // The geofence always gets the freshest reading (pushFix throttles the
        // network call itself); only the *rendered* fix is coalesced.
        if (isMeaningfulMove(get().fix, fix)) set({ fix, locationError: null })
        else if (get().locationError) set({ locationError: null })
        void get().pushFix(fix)
      },
      (err) => set({ locationError: err.message }),
    )
    set({ watching: true })
  },

  stopWatching() {
    stopWatch?.()
    stopWatch = null
    set({ watching: false })
  },

  async pushFix(fix, force = false) {
    if (!get().user) return
    const now = Date.now()
    if (!force && now - lastReportAt < MIN_REPORT_INTERVAL_MS) return
    lastReportAt = now

    try {
      const result = await api.reportPosition(fix.lat, fix.lon, fix.accuracyM)
      set({ nearbyIds: new Set(result.nearby_reminder_ids) })

      // One notification per store, listing everything that store can satisfy.
      for (const trigger of result.triggered) {
        const items = trigger.items.map((i) => i.title).join(', ')
        await showNudge(
          `You're near ${trigger.store_name}`,
          items,
          `store-${trigger.store_id}`,
        )
      }
      if (result.triggered.length > 0) await get().refresh()
    } catch (err) {
      set({ error: err instanceof Error ? err.message : String(err) })
    }
  },

  setError(msg) {
    set({ error: msg })
  },

  completeOnboarding() {
    const { user } = get()
    markOnboarded(user?.id)
    set({ onboarded: true })
  },

  clearTierLimit() {
    set({ tierLimitHit: false })
  },
}))

/** Active reminders count against the free plan's 5-reminder cap. */
export function activeCount(reminders: Reminder[]): number {
  return reminders.filter((r) => r.active && !r.completed_at).length
}

export const FREE_LIMIT = 5
